import React from "react";
import { useTheme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import { useMediaQuery, Box } from "@mui/material";
import PropTypes from "prop-types";
import CourseCardReattemptMessage from "./CourseCardReattemptMessage";
import { assignmentStatuses } from "../../constants/index";
import CourseCardGridType from "./CourseCardGridType";
import CourseCardListType from "./CourseCardListType";
import CourseCardDueDate from "./CourseCardDueDate";
import CourseFlag from "../Icons/CourseFlag";
import CourseCardLocation from "./CourseCardLocation";
import CourseCardDuration from "./CourseCardDuration";

const useStyles = makeStyles(theme => ({
  buttonsContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    "& > :not(:first-child)": {
      marginTop: theme.spacing(1.5)
    },
    [theme.breakpoints.only("sm")]: {
      marginTop: theme.spacing(4.5)
    }
  }
}));

const CourseCard = ({
  courseType,
  typeName,
  buttons,
  activitiesSummary,
  expansionPanel,
  imageSrc,
  prerequisites,
  gridView,
  flags,
  title,
  dueDate,
  scheduledDate,
  scheduledTime,
  endDate,
  endTime,
  condensed,
  passingPostScore,
  postScore,
  status,
  hasNote,
  location,
  failedStatus,
  absent,
  courseDescription,
  passFail,
  number,
  manualName,
  duration,
  isSelfAssigned,
  buttonsVariant
}) => {
  const theme = useTheme();
  const classes = useStyles();
  // Screen size used for determining layout of Course Card
  const xs = useMediaQuery(theme.breakpoints.down("sm"));
  const assignmentDueDate = (
    <CourseCardDueDate
      dueDate={dueDate}
      scheduledTime={scheduledTime}
      scheduledDate={scheduledDate}
      courseType={courseType}
      endDate={endDate}
      endTime={endTime}
    />
  );

  const assignmentDuration = <CourseCardDuration duration={duration} />;

  const assignmentReattemptMessage = (status === assignmentStatuses.failed ||
    status === assignmentStatuses.pending ||
    !!failedStatus) && (
    <CourseCardReattemptMessage
      postScore={postScore}
      passingPostScore={passingPostScore}
      assignmentStatus={status}
      passFail={passFail}
      isAbsent={absent}
    />
  );
  const assignmentLocation = location && (
    <CourseCardLocation courseType={courseType} location={location} />
  );

  const courseButtons = (
    <div className={classes.buttonsContainer}>{buttons}</div>
  );
  const courseFlags = flags?.length > 0 && (
    <Box display="flex" alignItems="center">
      {flags.map((courseFlag, index) => (
        <CourseFlag key={index} courseFlag={courseFlag} />
      ))}
    </Box>
  );

  const courseProps = {
    condensed,
    activitiesSummary,
    flags: courseFlags,
    title,
    prerequisites,
    courseType,
    typeName,
    buttons: courseButtons,
    hasNote,
    expansionPanel,
    imageSrc,
    dueDate: assignmentDueDate,
    reattemptMessage: assignmentReattemptMessage,
    location: assignmentLocation,
    courseDescription,
    manualName,
    number,
    duration: assignmentDuration,
    isSelfAssigned,
    buttonsVariant
  };

  return xs || gridView ? (
    <CourseCardGridType {...courseProps} />
  ) : (
    <CourseCardListType {...courseProps} />
  );
};

export default CourseCard;

CourseCard.propTypes = {
  buttons: PropTypes.node,
  expansionPanel: PropTypes.node,
  icon: PropTypes.node,
  imageSrc: PropTypes.string,
  title: PropTypes.string,
  prerequisites: PropTypes.array,
  activitiesSummary: PropTypes.node,
  gridView: PropTypes.bool,
  dueDate: PropTypes.string,
  scheduledDate: PropTypes.string,
  scheduledTime: PropTypes.string,
  number: PropTypes.string,
  manualName: PropTypes.string,
  status: PropTypes.oneOf(Object.values(assignmentStatuses)),
  failedStatus: PropTypes.bool,
  hasNote: PropTypes.bool,
  condensed: PropTypes.bool,
  location: PropTypes.object,
  isSelfAssigned: PropTypes.bool,
  buttonsVariant: PropTypes.oneOf(["wide", "narrow"])
};

CourseCard.defaultProps = {
  isSelfAssigned: true
};
